import { computed, h, defineComponent, ref, onUnmounted } from 'vue';
// @ts-ignore
import { BTableColumn, BTable } from 'buefy/dist/esm/table';
import { t } from '@/ui/i18n/i18n';
import { column } from '@/ui/components';
import { RouteName, router, useCurrentRoute } from '@/ui/routes';
import { TableColumnDelete } from './TableColumnDelete';
import { NothingToDisplayMessage } from '../message/NothingToDisplayMessage';
import { SortDirection } from '@/ui/types';
import { Lens } from 'monocle-ts';
import { openNewTab, isNewTabKey } from '@/ui/helpers';

export interface ResourceTableProps<T = unknown> {
  titleColumnLabel: string;
  editRoute: RouteName | Function;
  entityIdPath?: Array<string>;
  list: BaseEntity[]; // makes sure we have at least an ID and title
  deleteFn?: Function;
  permissionName: 'view' | 'update';
  sort?: string;
  dir?: SortDirection;
  sortableTitle: boolean;
  clickableTitle?: boolean;
  checkable?: boolean;
  selectResources?: T[];
  isRowCheckable?: (row: T) => boolean;
  selectedRecords: T[];
  isHoverable: boolean;
}

export interface TableRow<T> {
  row: T;
}

export const resourceTableProps = [
  'list', 'editRoute', 'entityIdPath',
  'deleteFn', 'titleColumnLabel', 'permissionName',
  'sort', 'dir', 'sortableTitle', 'clickableTitle',
  'checkable', 'selectResources', 'isRowCheckable',
  'selectedRecords', 'isHoverable',
];

export const ResourceTable = defineComponent<ResourceTableProps>({
  name: 'ResourceTable',
  props: resourceTableProps,
  setup(props, context) {
    //default permission should be view, not update
    const permissionName = props.permissionName || 'view';

    const deleted = ref<number[]>([]);
    const closed = ref<number[]>([]);
    const clickable = props.clickableTitle ?? true;
    const lastHoveredRow = ref<BaseEntity>();
    const currentRoute = useCurrentRoute();
    const isHoverable = props.isHoverable ?? true;

    const filteredList = computed(() => props.list.filter(
      row => !closed.value.includes(row.id),
    ));

    const isEnableNewTab = computed(() => currentRoute.value.name === 'forms' || currentRoute.value.name === 'issueIndex');

    const handleDelete = (id: number, entity: BaseEntity) => {
      deleted.value.push(id);
      closed.value.push(id);
      context.emit('deleted', { id, entity });
    };

    const handleClose = (id: number) => {
      closed.value.push(id);
    };

    /**
     * if ID is not the main id of the entity but another property in the row, example for legacy v1:
     * editRoute: 'issueEdit',
     * entityIdPath: ['data', 'issue', 'id'],
     */
    const entityId = (entity: BaseEntity, entityIdPath?: Array<string>) => {
      if (!entityIdPath) {
        return entity.id.toString();
      }

      const lens = Lens.fromPath<unknown>()(entityIdPath as [never]);
      return lens.get(entity);
    };

    const preparedEditRoute = (rowProps: TableRow<BaseEntity>, editRoute: RouteName | Function): RouteName => {
      if (typeof editRoute === 'function') {
        return editRoute(rowProps);
      }

      return editRoute;
    };

    const isNotEditableResource = (row: BaseEntity) => !clickable
      || deleted.value.includes(row.id)
      || !row.authorizedTo?.[permissionName];

    const getRowClass = (row: BaseEntity) => {
      if (isNotEditableResource(row)) {
        return 'is-disabled';
      }

      return null;
    };

    const onHandleClick = (event: MouseEvent) => {
      if (lastHoveredRow.value && isHoverable && filteredList.value.includes(lastHoveredRow.value)) {
        const routeObject = {
          name: preparedEditRoute({ row: lastHoveredRow.value }, props.editRoute),
          params: { id: entityId(lastHoveredRow.value, props.entityIdPath) as string },
        };

        if (isNewTabKey(event)) {
          const { href } = router.resolve(routeObject);
          openNewTab(href);
        } else {
          if (isNotEditableResource(lastHoveredRow.value)) {
            context.emit('click', lastHoveredRow.value);
            return;
          }

          router.push(routeObject);
        }
      }
    };

    if (isEnableNewTab.value) {
      window.addEventListener('click', onHandleClick);
    }

    onUnmounted(() => {
      window.removeEventListener('click', onHandleClick);
    });

    return function render() {
      return h(BTable, {
        class: [
          'table--resource-table table is-fullwidth',
          { 'is-hoverable': isHoverable },
        ],
        ref: 'table',
        props: {
          data: filteredList.value,
          backendSorting: true,
          backendPagination: true,
          backendFiltering: true,
          sortIcon: 'angle-up',
          iconPack: 'far',
          defaultSortDirection: 'asc',
          defaultSort: [props.sort, props.dir],
          checkable: props.checkable,
          isRowCheckable: props.isRowCheckable,
          checkedRows: props.selectedRecords,
          rowClass: getRowClass,
        },
        scopedSlots: {
          empty: ('empty' in context.slots)
            ? context.slots.empty
            : () => h(NothingToDisplayMessage),
        },
        on: {
          'update:checkedRows': (rows: unknown[]) => context.emit('update:checkedRows', rows),
          sort: (field: string, dir: string) => context.emit('sort', field, dir),
          select: (row: { id: number }) => context.emit('select', row),
          mouseenter: (row: BaseEntity) => {
            lastHoveredRow.value = row;
          },
          mouseleave: () => {
            lastHoveredRow.value = undefined;
          },
          click: (row: BaseEntity) => {
            if (isHoverable) {
              if (isNotEditableResource(row) || isEnableNewTab.value || !props.editRoute) {
                context.emit('click', row);
                return;
              }

              router.push({
                name: preparedEditRoute({ row }, props.editRoute),
                params: { id: entityId(row, props.entityIdPath) as string },
              });
            }
          },
        },
      }, [
        context.slots.subtitle
          ? [(rowProps: TableRow<BaseEntity>) => h('div', context.slots.subtitle?.(rowProps))]
          : null,
        context.slots.title ? column(
          {
            field: 'title',
            label: props.titleColumnLabel || t('common.title'),
            sortable: props.sortableTitle,
            cellClass: 'table__title-cell',
          },
          { default: (rowProps: TableRow<BaseEntity>) => context.slots.title?.(rowProps) },
        ) : null,
        (context.slots.default)
          ? [context.slots.default()]
          : null,
        (context.slots.actions)
          ? [context.slots.actions()]
          : [],
        props.deleteFn ? h(
          BTableColumn,
          {
            props: {
              numeric: true,
              label: '',
              field: 'delete',
              cellClass: 'rightmost',
            },
            scopedSlots: {
              default: (rowProps: TableRow<BaseEntity>) => [
                h('div', {
                  class: 'table__actions-cell',
                }, [
                  context.slots.beforeDeleteButton?.(rowProps),
                  rowProps.row.authorizedTo?.delete
                    ? [h(TableColumnDelete, {
                      props: {
                        id: entityId(rowProps.row, props.entityIdPath),
                        deleteFn: props.deleteFn,
                        rowProps,
                      },
                      on: {
                        deleted: () => handleDelete(rowProps.row.id, rowProps.row),
                        closed: () => handleClose(rowProps.row.id),
                      },
                    })]
                    : [],
                ]),
              ],
            },
          },
        ) : null,
      ]);
    };
  },
});
